import { useCallback, useMemo, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import SaveIcon from '@mui/icons-material/Save'

import PageHeader from '../../Shared/PageHeader'
import DataTable from '../../Shared/DataTable'
import LoadingBackdrop from '../../Shared/LoadingBackdrop'
import { useFeedback } from '../../Shared/FeedbackSnackbar'
import { searchProductService, setConversionFactor } from '../../../services/productService'
import { mensagemDeErro } from '../../../services/apiClient'
import {
  TIPOS_DE_PECA,
  converteEmEmbalagem,
  embalagensNecessarias,
  rotuloDoTipoDePeca,
} from '../../../utils/quebras'
import { formatNumber } from '../../../utils/format'
import useDebounce from '../../../utils/useDebounce'
import PermissionComponent from '../../../permissions'
import { PERMISSOES } from '../../../const/permissoes'

const EMPTY_ITEM = { product: null, piece: '', typePiece: 'PECA' }

/** Quantidade de exemplo da prévia — só para mostrar o efeito do fator. */
const EXEMPLO = 100

const ConversionFactor = () => {
  const { notify, FeedbackSnackbar } = useFeedback()

  const [item, setItem] = useState(EMPTY_ITEM)
  const [lista, setLista] = useState([])
  const [products, setProducts] = useState([])
  const [termo, setTermo] = useState('')
  const [buscando, setBuscando] = useState(false)
  const [salvando, setSalvando] = useState(false)

  const changeItem = (campo, valor) => setItem(atual => ({ ...atual, [campo]: valor }))

  const buscarProdutos = useCallback(async () => {
    setBuscando(true)
    try {
      const response = await searchProductService({
        SearchWord: termo || undefined,
        limit: 20,
        offset: 0,
      })
      setProducts(response.data.result || [])
    } catch (error) {
      setProducts([])
    } finally {
      setBuscando(false)
    }
  }, [termo])

  useDebounce(() => buscarProdutos(), 400, [termo])

  const precisaDeFator = converteEmEmbalagem(item.typePiece)
  const pieceNumero = Number(item.piece)
  const fatorInvalido = precisaDeFator && (!pieceNumero || pieceNumero <= 0)

  const previa = useMemo(() => {
    if (!precisaDeFator || fatorInvalido) return null
    return embalagensNecessarias(EXEMPLO, pieceNumero)
  }, [precisaDeFator, fatorInvalido, pieceNumero])

  const selecionarProduto = produto => {
    if (!produto) {
      setItem(EMPTY_ITEM)
      return
    }
    // Traz o fator já cadastrado para editar, em vez de começar do zero e
    // apagar em silêncio o que estava lá.
    setItem({
      product: produto,
      piece: produto.piece ?? '',
      typePiece: produto.typePiece || 'PECA',
    })
  }

  const adicionar = () => {
    if (!item.product) {
      notify('Escolha um produto.', 'warning')
      return
    }
    if (fatorInvalido) {
      notify('Produto contado em caixa precisa da quantidade por embalagem.', 'warning')
      return
    }
    if (lista.some(linha => linha.product.id === item.product.id)) {
      notify('Este produto já está na lista.', 'warning')
      return
    }
    setLista(atual => [
      ...atual,
      {
        product: item.product,
        piece: precisaDeFator ? pieceNumero : pieceNumero || null,
        typePiece: item.typePiece,
      },
    ])
    setItem(EMPTY_ITEM)
    setTermo('')
  }

  const salvar = async () => {
    setSalvando(true)
    try {
      await setConversionFactor(
        lista.map(linha => ({
          productId: linha.product.id,
          piece: linha.piece || null,
          typePiece: linha.typePiece,
        }))
      )
      notify(`Fator de conversão salvo para ${lista.length} produto(s).`, 'success')
      setLista([])
    } catch (error) {
      notify(mensagemDeErro(error, 'Não foi possível salvar o fator de conversão.'), 'error')
    } finally {
      setSalvando(false)
    }
  }

  const columns = [
    {
      id: 'actions',
      label: '',
      width: 60,
      element: row => (
        <Tooltip title="Remover da lista">
          <IconButton
            size="small"
            color="error"
            onClick={() => setLista(atual => atual.filter(linha => linha.product.id !== row.product.id))}
          >
            <DeleteForeverIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
    { id: 'name', label: 'Produto', element: row => row.product.name || '—' },
    { id: 'reference', label: 'Referência', element: row => row.product.reference || '—' },
    { id: 'sku', label: 'SKU', element: row => row.product.sku || '—' },
    {
      id: 'piece',
      label: 'Qtd. por embalagem',
      align: 'right',
      element: row => (row.piece ? formatNumber(row.piece) : '—'),
    },
    {
      id: 'typePiece',
      label: 'Contado em',
      element: row => (
        <Chip
          size="small"
          label={rotuloDoTipoDePeca(row.typePiece)}
          color={converteEmEmbalagem(row.typePiece) ? 'info' : 'default'}
        />
      ),
    },
  ]

  return (
    <Box>
      <PageHeader
        title="Fator de Conversão"
        subtitle="Quanto cabe na embalagem de cada produto. Orienta em quantas quebras dividir uma ordem."
        actions={
          <PermissionComponent permissao={PERMISSOES.PRODUTO_GERENCIAR}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={lista.length === 0 || salvando}
              onClick={salvar}
            >
              Salvar ({lista.length})
            </Button>
          </PermissionComponent>
        }
      />

      <Alert severity="info" sx={{ mb: 3 }}>
        A quantidade da ordem e das quebras continua sempre em <strong>peças</strong>. O fator
        serve para sugerir o número de quebras — uma por embalagem — e para rotular as telas de
        produção. Só o tipo <strong>Caixa</strong> converte.
      </Alert>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} md={5}>
              <Autocomplete
                size="small"
                options={products}
                value={item.product}
                loading={buscando}
                loadingText="Procurando..."
                filterOptions={opcoes => opcoes}
                getOptionLabel={option =>
                  [option.name, option.reference, option.sku].filter(Boolean).join(' · ')
                }
                isOptionEqualToValue={(option, selected) => option.id === selected.id}
                onInputChange={(event, valor) => setTermo(valor)}
                onChange={(event, valor) => selecionarProduto(valor)}
                noOptionsText="Nenhum produto encontrado"
                renderInput={params => <TextField {...params} label="Procurar produto" />}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                select
                fullWidth
                size="small"
                label="Contado em"
                value={item.typePiece}
                disabled={!item.product}
                onChange={event => changeItem('typePiece', event.target.value)}
              >
                {TIPOS_DE_PECA.map(tipo => (
                  <MenuItem key={tipo.value} value={tipo.value}>
                    {tipo.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Qtd. por embalagem"
                inputProps={{ min: 1 }}
                value={item.piece}
                disabled={!item.product}
                error={fatorInvalido}
                onChange={event => changeItem('piece', event.target.value)}
                helperText={
                  fatorInvalido ? 'Obrigatório para Caixa' : precisaDeFator ? ' ' : 'Opcional'
                }
              />
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<AddIcon />}
                disabled={!item.product || fatorInvalido}
                onClick={adicionar}
              >
                Adicionar
              </Button>
            </Grid>
          </Grid>

          {item.product && (
            <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
              <Chip
                size="small"
                variant="outlined"
                label={`Fator atual: ${
                  item.product.piece
                    ? `${formatNumber(item.product.piece)} por ${rotuloDoTipoDePeca(
                        item.product.typePiece
                      ).toLowerCase()}`
                    : 'nenhum'
                }`}
              />
              {previa !== null && (
                <Chip
                  size="small"
                  color="info"
                  label={`Exemplo: ${EXEMPLO} peças = ${previa} ${
                    previa === 1 ? 'caixa' : 'caixas'
                  }`}
                />
              )}
            </Stack>
          )}

          {previa !== null && EXEMPLO % pieceNumero !== 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              A última embalagem fica incompleta e mesmo assim conta — caixa partida ainda é caixa
              a produzir.
            </Typography>
          )}
        </CardContent>
      </Card>

      <DataTable
        columns={columns}
        rows={lista}
        emptyMessage="Nenhum produto na lista. Escolha um produto acima e clique em Adicionar."
        rowKey={row => row.product.id}
      />

      <LoadingBackdrop open={salvando} message="Salvando..." />
      <FeedbackSnackbar />
    </Box>
  )
}

export default ConversionFactor
